import { useState } from "react";
import type { IBatchTemplate } from "@/api/Templates";
import { ExpansionPanel } from "@medoma/components/ExpansionPanel/ExpansionPanel";
import styles from "./BatchTemplate.module.scss";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { HeadingWithEllipsis } from "@medoma/components/Heading/Heading";
import { CreateActivitiesFromBatchTemplateForm } from "./CreateActivitiesFromBatchTemplateForm/CreateActivitiesFromBatchTemplateForm";
import { activityRequirementStatusSchema } from "@medoma/models/activities";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";

const Competences = ({
  competences,
}: {
  competences: IMedicalCompetence[];
}) => {
  return (
    <div className={styles.competences}>
      {competences.map((competence) => (
        <CompetenceChip
          key={competence}
          competence={competence}
          state={activityRequirementStatusSchema.enum.neutral}
        />
      ))}
    </div>
  );
};

const Header = ({
  title,
  requiredCompetences,
}: {
  title: string;
  requiredCompetences: IMedicalCompetence[];
}) => {
  return (
    <div className={styles.header}>
      <CategoryIcon category={"MultipleCategories"} size="small" />
      <HeadingWithEllipsis
        level="h2"
        size="base"
        weight="regular"
        maxWidth="unset"
      >
        {title}
      </HeadingWithEllipsis>
      <Competences competences={requiredCompetences} />
    </div>
  );
};

const BatchTemplate = ({ template }: { template: IBatchTemplate }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayedTitle = template.template.title;
  const requiredCompetences = Array.from(
    new Set(
      template.template.activities.flatMap(
        (activity) => activity.requiredCompetences ?? [],
      ),
    ),
  );

  return (
    <ExpansionPanel
      trigger={
        <Header
          title={displayedTitle}
          requiredCompetences={requiredCompetences}
        />
      }
      triggerAriaLabel={displayedTitle}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    >
      <div className={styles.content}>
        <CreateActivitiesFromBatchTemplateForm
          template={template}
          onSuccess={() => setIsExpanded(false)}
        />
      </div>
    </ExpansionPanel>
  );
};

export { BatchTemplate };
